import { inject, Injectable } from '@angular/core';
import { GlobalWebsocketService } from '@approot/global-websocket';
import { Entity } from '@approot/models';
import {
  AlertsService,
  ConfigService,
  TradesCaptureService,
  UserStorageService
} from '@approot/services';
import { clone, sleep } from '@approot/utilities';
import { isEqual } from 'lodash';
import { Observable, ReplaySubject, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { mapHeaders } from './pnl-blotter.funcs';
import { PNLBlotterPayload, PNLBlotterState } from './pnl-blotter.types';
import { Row, RunningState, WSRow } from '../shared/utils';

// Note that "entities" and "headers" are not included here as they won't change once set.
const DefaultPNLBlotterState: PNLBlotterState = {
  uuid: null,
  rows: [],
  nItems: null,
  runningState: RunningState.Idle,
  info: null
};

@Injectable({
  providedIn: 'root'
})
export class PNLBlotterService {
  public stateChanged: Observable<PNLBlotterState>;
  private state: PNLBlotterState = {};

  // this service is permanent but the component is recreated on each tab navigation
  // so we need to use a ReplaySubject to keep the state
  private state$ = new ReplaySubject<PNLBlotterState>(1);
  private pnlSub: Subscription = null;
  private timer: any = null;
  private websocketService = inject(GlobalWebsocketService);
  private tradesCaptureService = inject(TradesCaptureService);
  private alertsService = inject(AlertsService);
  private configService = inject(ConfigService);
  private userStorageService = inject(UserStorageService);

  constructor() {
    this.setState({
      ...DefaultPNLBlotterState,
      entities: [],
      headers: []
    });
    this.stateChanged = this.state$.asObservable();
    this.loadEntities();
    this.loadPNLBlotterHeaders();
  }

  public update(state: PNLBlotterState): void {
    this.setState(state);
  }

  public async runReport(payload: PNLBlotterPayload) {
    // backwards compatibility
    payload.date = payload.end_date;
    payload.time = payload.end_time;
    const uuid = await this.websocketService.sendMessage({
      action: 'apiv2_pnlReport',
      payload
    });
    console.log('Running PNL Report for', payload, uuid);
    this.setState(
      {
        ...DefaultPNLBlotterState,
        uuid,
        selectedEntry: payload,
        timestamp: null
      },
      false
    );
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timer = setTimeout(() => {
      if (this.state.runningState === RunningState.Running) {
        this.cancelReport('Report timed out');
        console.log('PNL Report timed out');
        this.alertsService.addAlert({
          type: 'error',
          message: 'PNL Report timed out'
        });
        this.userStorageService.ping();
      }
    }, 5 * 60_000);
  }

  public cancelReport(info: string = null) {
    this.unsubscribe();
    this.setState({ ...DefaultPNLBlotterState, info });
    clearTimeout(this.timer);
    this.timer = null;
  }

  /****<PRIVATE>**************************************************************/

  private loadEntities(): void {
    this.tradesCaptureService
      .getEntities()
      .pipe(take(1))
      .subscribe({
        next: (entities: Entity[]) => {
          const e = entities
            .map(e => e.identifier)
            .sort((a, b) =>
              a.localeCompare(b, 'en', {
                sensitivity: 'accent'
              })
            );
          this.setState({ entities: e });
        }
      });
  }

  private loadPNLBlotterHeaders(): void {
    this.configService
      .getAgGridConfig()
      .pipe(
        take(1),
        map(config => {
          const setting = config.find(
            c => c.identifier === 'defaultPnlBlotterHeaders'
          );
          const headers = config.find(c => c.identifier === 'headers');
          return mapHeaders(setting, headers);
        })
      )
      .subscribe({
        next: headers => this.setState({ headers })
      });
  }

  private setState(state: PNLBlotterState, emit = true) {
    const ouuid = this.state.uuid;
    const newState = { ...this.state, ...state };
    if (isEqual(newState, this.state)) {
      return;
    }

    this.state = clone(newState, true);
    if (emit) {
      this.state$.next(this.state);
    }
    if (state.uuid && state.uuid !== ouuid) {
      this.subscribeToPNLBlotter(state.uuid);
    }
  }

  private unsubscribe() {
    this.pnlSub?.unsubscribe();
    this.pnlSub = null;
  }

  private subscribeToPNLBlotter(uuid: string) {
    this.unsubscribe();
    this.setState({
      uuid,
      rows: [],
      nItems: null,
      runningState: RunningState.Running
    });

    const resubscribe = (iteration: number) => {
      if (iteration > 5) {
        console.error(
          'Failed to subscribe to PNL Blotter - max retries exceeded'
        );
        return;
      }
      this.pnlSub?.unsubscribe();
      this.pnlSub = this.websocketService
        .getListener({ uuid, take: Infinity })
        .pipe(
          map((row: WSRow) => {
            return {
              ...row.payload,
              uuid: row.uuid
            } as Row;
          })
        )
        .subscribe({
          next: r => {
            if (r.error) {
              this.handleError(r, true);
            } else if (r.warning) {
              this.handleError(r, false);
            } else if (r.info) {
              this.handleInfo(r);
            } else if (r.item) {
              this.handleData(r);
            }
          },
          complete: async () => {
            console.log('PNL Blotter subscription completed');
            await sleep(500);
            resubscribe(iteration + 1);
          },
          error: async err => {
            console.error(err);
            await sleep(500);
            resubscribe(iteration + 1);
          }
        });
    };

    resubscribe(0);
  }

  private handleError(row: Row, abort: boolean) {
    if (abort || (row.warning && row.message === 'No events')) {
      this.unsubscribe();
      this.setState(DefaultPNLBlotterState);
    }
    this.alertsService.addAlert({
      type: row.error ? 'error' : 'warning',
      message: row.message || 'There was a problem running the PNL report',
      config: {
        closeButton: true,
        tapToDismiss: true,
        timeOut: 3000
      }
    });
    this.userStorageService.ping();
  }

  private handleInfo(row: Row) {
    this.setState({ info: row.message });
  }

  private handleData(row: Row) {
    const curr = this.state.rows;
    const rows = [...curr, row.item];
    let runningState = this.state.runningState;
    let info = this.state.info;
    if (rows.length === row.nItems) {
      this.unsubscribe();
      runningState = RunningState.Idle;
      info = 'Report loaded';
      this.setState({ timestamp: new Date().getTime() });
      this.userStorageService.ping();
    }
    this.setState({ rows, nItems: row.nItems, info, runningState });
  }
}
